<template>
  <div class="online-users">
    <div class="page-header">
      <h2>在线用户监控</h2>
      <div class="header-actions">
        <el-button @click="loadOnlineUsers" :loading="loading">刷新</el-button>
        <el-switch
          v-model="autoRefresh"
          active-text="自动刷新"
          @change="toggleAutoRefresh"
        />
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-number">{{ stats.total_online }}</div>
          <div class="stat-label">在线用户</div>
        </div>
        <div class="stat-icon online">
          <el-icon><User /></el-icon>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-number">{{ stats.device_types }}</div>
          <div class="stat-label">设备类型</div>
        </div>
        <div class="stat-icon device">
          <el-icon><Monitor /></el-icon>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-number">{{ stats.ip_segments }}</div>
          <div class="stat-label">IP段数</div>
        </div>
        <div class="stat-icon network">
          <el-icon><Connection /></el-icon>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-number">{{ formatTime(lastUpdateTime) }}</div>
          <div class="stat-label">最后更新</div>
        </div>
        <div class="stat-icon time">
          <el-icon><Clock /></el-icon>
        </div>
      </el-card>
    </div>

    <!-- 在线用户列表 -->
    <el-card class="users-card">
      <template #header>
        <div class="card-header">
          <span>在线用户列表</span>
          <el-tag type="success">{{ onlineUsers.length }} 人在线</el-tag>
        </div>
      </template>

      <el-table 
        :data="onlineUsers" 
        :loading="loading"
        stripe
        style="width: 100%"
      >
        <el-table-column label="用户信息" width="250">
          <template #default="{ row }">
            <div class="user-info">
              <div class="device-name">{{ row.device_name || '未知设备' }}</div>
              <div class="user-meta">
                <el-tag size="small">{{ row.device_type }}</el-tag>
                <span class="user-id">ID: {{ row.id }}</span>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="卡密信息" width="200">
          <template #default="{ row }">
            <div>
              <div class="card-key">{{ row.card_key }}</div>
              <el-tag :type="getCardTypeColor(row.card_type)" size="small">
                {{ getCardTypeName(row.card_type) }}
              </el-tag>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="ip_address" label="IP地址" width="120" />
        <el-table-column prop="location" label="位置" width="100" />

        <el-table-column label="会话信息" width="200">
          <template #default="{ row }">
            <div class="session-info">
              <div>登录: {{ formatTime(row.login_at) }}</div>
              <div>心跳: {{ formatTime(row.last_heartbeat) }}</div>
              <div class="heartbeat-status">
                <el-tag 
                  :type="row.minutes_since_heartbeat <= 1 ? 'success' : 'warning'" 
                  size="small"
                >
                  {{ row.minutes_since_heartbeat }}分钟前
                </el-tag>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="在线时长" width="100">
          <template #default="{ row }">
            {{ getOnlineDuration(row.login_at) }}
          </template>
        </el-table-column>

        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="viewUserDetail(row)">详情</el-button>
            <el-button 
              size="small" 
              type="warning" 
              @click="kickUser(row)"
            >
              踢出
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 用户详情对话框 -->
    <el-dialog
      v-model="showDetailDialog"
      title="在线用户详情"
      width="700px"
    >
      <div v-if="selectedUser" class="user-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="用户ID">{{ selectedUser.id }}</el-descriptions-item>
          <el-descriptions-item label="设备名称">{{ selectedUser.device_name }}</el-descriptions-item>
          <el-descriptions-item label="设备类型">{{ selectedUser.device_type }}</el-descriptions-item>
          <el-descriptions-item label="设备ID">{{ selectedUser.device_id }}</el-descriptions-item>
          <el-descriptions-item label="IP地址">{{ selectedUser.ip_address }}</el-descriptions-item>
          <el-descriptions-item label="位置">{{ selectedUser.location }}</el-descriptions-item>
          <el-descriptions-item label="会话令牌">
            <code>{{ selectedUser.session_token }}</code>
          </el-descriptions-item>
          <el-descriptions-item label="登录时间">{{ formatTime(selectedUser.login_at) }}</el-descriptions-item>
          <el-descriptions-item label="最后心跳">{{ formatTime(selectedUser.last_heartbeat) }}</el-descriptions-item>
          <el-descriptions-item label="会话过期">{{ formatTime(selectedUser.expires_at) }}</el-descriptions-item>
          <el-descriptions-item label="在线时长">{{ getOnlineDuration(selectedUser.login_at) }}</el-descriptions-item>
          <el-descriptions-item label="卡密信息">
            <div>
              <div>{{ selectedUser.card_key }}</div>
              <el-tag :type="getCardTypeColor(selectedUser.card_type)" size="small">
                {{ getCardTypeName(selectedUser.card_type) }}
              </el-tag>
            </div>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { User, Monitor, Connection, Clock } from '@element-plus/icons-vue'
import { userAPI } from '@/services/api'

export default {
  name: 'OnlineUsers',
  components: {
    User,
    Monitor,
    Connection,
    Clock
  },
  setup() {
    const loading = ref(false)
    const autoRefresh = ref(true)
    const showDetailDialog = ref(false)
    const onlineUsers = ref([])
    const selectedUser = ref(null)
    const lastUpdateTime = ref(new Date())
    let refreshTimer = null

    const stats = reactive({
      total_online: 0,
      device_types: 0,
      ip_segments: 0
    })

    // 加载在线用户
    const loadOnlineUsers = async () => {
      try {
        loading.value = true
        const response = await userAPI.getOnlineUsers()
        
        if (response.success) {
          onlineUsers.value = response.data.online_users
          Object.assign(stats, response.data.stats)
          lastUpdateTime.value = new Date()
        } else {
          ElMessage.error(response.message || '获取在线用户失败')
        }
      } catch (error) {
        ElMessage.error('获取在线用户失败: ' + error.message)
      } finally {
        loading.value = false
      }
    }

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        refreshTimer = setInterval(loadOnlineUsers, 30000) // 30秒刷新一次
      } else {
        if (refreshTimer) {
          clearInterval(refreshTimer)
          refreshTimer = null
        }
      }
    }

    // 查看用户详情
    const viewUserDetail = (user) => {
      selectedUser.value = user
      showDetailDialog.value = true
    }

    // 踢出用户
    const kickUser = async (user) => {
      try {
        await ElMessageBox.confirm(
          `确定要踢出用户 "${user.device_name}" 吗？`,
          '确认踢出',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await userAPI.kickUser(user.id)
        if (response.success) {
          ElMessage.success('用户已被踢出')
          loadOnlineUsers()
        } else {
          ElMessage.error(response.message || '踢出用户失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('踢出用户失败: ' + error.message)
        }
      }
    }

    // 获取卡密类型名称
    const getCardTypeName = (type) => {
      const names = {
        'AI_HOST_BASIC': 'AI主机基础版',
        'AI_HOST_PRO': 'AI主机专业版',
        'AI_BOX_BASIC': 'AI盒子基础版',
        'AI_BOX_PRO': 'AI盒子专业版',
        'ALL_IN_ONE': '全功能版'
      }
      return names[type] || type
    }

    // 获取卡密类型颜色
    const getCardTypeColor = (type) => {
      const colors = {
        'AI_HOST_BASIC': '',
        'AI_HOST_PRO': 'success',
        'AI_BOX_BASIC': 'info',
        'AI_BOX_PRO': 'warning',
        'ALL_IN_ONE': 'danger'
      }
      return colors[type] || ''
    }

    // 格式化时间
    const formatTime = (dateString) => {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    }

    // 计算在线时长
    const getOnlineDuration = (loginTime) => {
      if (!loginTime) return '-'
      
      const now = new Date()
      const login = new Date(loginTime)
      const diffMs = now - login
      const diffMins = Math.floor(diffMs / 60000)
      
      if (diffMins < 60) {
        return `${diffMins}分钟`
      } else if (diffMins < 1440) {
        const hours = Math.floor(diffMins / 60)
        const mins = diffMins % 60
        return `${hours}小时${mins}分钟`
      } else {
        const days = Math.floor(diffMins / 1440)
        const hours = Math.floor((diffMins % 1440) / 60)
        return `${days}天${hours}小时`
      }
    }

    onMounted(() => {
      loadOnlineUsers()
      toggleAutoRefresh(autoRefresh.value)
    })

    onUnmounted(() => {
      if (refreshTimer) {
        clearInterval(refreshTimer)
      }
    })

    return {
      loading,
      autoRefresh,
      showDetailDialog,
      onlineUsers,
      selectedUser,
      lastUpdateTime,
      stats,
      loadOnlineUsers,
      toggleAutoRefresh,
      viewUserDetail,
      kickUser,
      getCardTypeName,
      getCardTypeColor,
      formatTime,
      getOnlineDuration
    }
  }
}
</script>

<style scoped>
.online-users {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-actions {
  display: flex;
  gap: 15px;
  align-items: center;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  border-radius: 8px;
}

.stat-card :deep(.el-card__body) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.stat-icon.online {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stat-icon.device {
  background: linear-gradient(135deg, #409eff, #66b1ff);
}

.stat-icon.network {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
}

.stat-icon.time {
  background: linear-gradient(135deg, #909399, #a6a9ad);
}

.users-card {
  border-radius: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.device-name {
  font-weight: bold;
}

.user-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-id {
  font-size: 12px;
  color: #909399;
}

.card-key {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  margin-bottom: 4px;
}

.session-info {
  font-size: 12px;
  line-height: 1.4;
}

.heartbeat-status {
  margin-top: 4px;
}

.user-detail {
  margin-bottom: 20px;
}

.user-detail code {
  background: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 12px;
}
</style>
